import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useConfirm } from 'material-ui-confirm'
import moment from 'moment'

import ImageUploader from 'components/ImageUploader'
import JanReader from 'components/JanReader'
import { deleteDoc, generateId, setDoc, updateDoc } from 'lib/firestore'
import { client, isMobile } from 'utils'
import { NO_DEADLINE_DATE, Role, categories } from 'utils/constant'

// フォームに挿入する形式のデータに変換
const convert = (data, locations) => {
  const _data = { ...data }
  if (_data.id) {
    if (_data.expire) {
      const expire = moment(data.expire).format('YYYY-MM-DD')
      _data.expire = expire !== '9999-12-31' ? expire : ''
    }
    if (data.location) {
      _data.locationId = data.location.id
    }
  } else {
    _data.price = 0
    _data.amount = 1
    _data.locationId = locations[0].id
  }
  return _data
}

const validateNaturalNumber = v => {
  const number = Number(v)
  if (isNaN(number)) return false
  if (!Number.isInteger(number)) return false
  if (number < 0) return false
  return true
}

const validateEmptyString = v => {
  return v.trim() !== ''
}

export default function StockDialog({
  account,
  locations,
  data,
  admin,
  onClose,
  onCreate,
  onEdit,
  onArchive,
  onRecover,
  onDelete,
}) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: convert(data, locations) })
  const [isContinuous, setIsContinuous] = useState(false)
  const [open, setOpen] = useState(false)
  const [id, setId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [readerOpen, setReaderOpen] = useState(false)
  const image = watch('image')
  const confirm = useConfirm()

  useEffect(() => {
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!data.id) {
      setId(generateId(`accounts/${account.id}/stocks`))
    }
  }, [account, data])

  useEffect(() => {
    register('image')
  }, [register])

  const activeLocations = useMemo(() => {
    if (data.id && !locations.some(x => x.id === data.location.id)) {
      return locations.concat([data.location])
    }
    return locations
  }, [data, locations])

  // TODO: convert と onSubmit のデータ変換処理部分のリファクタリング
  const onSubmit = async values => {
    const _values = { ...values }
    _values.expire = new Date(values.expire !== '' ? values.expire : NO_DEADLINE_DATE)
    _values.image = _values.image || null
    _values.price = parseInt(_values.price)
    _values.amount = parseInt(_values.amount)
    const location =
      account.role === Role.LOCATION_MEMBER.value
        ? locations[0]
        : {
            id: _values.locationId,
            name: locations.find(x => x.id === _values.locationId).name,
          }
    delete _values.locationId
    if (data.id) {
      await updateDoc(`accounts/${account.id}/stocks/${data.id}`, { ..._values, location })
      onEdit()
    } else {
      await setDoc(`accounts/${account.id}/stocks/${id}`, {
        ..._values,
        location,
        account: { id: account.id, name: account.name },
        deleted: false,
      })
      onCreate()
    }
    if (isContinuous) {
      // image, category, purchase, locationId, storage 以外をリセット
      reset({
        ...values,
        name: '',
        code: '',
        standard: '',
        price: 0,
        amount: 1,
        expire: '',
        memo: '',
      })
      setId(generateId(`accounts/${account.id}/stocks`))
    } else {
      setOpen(false)
    }
  }

  const handleDelete = async () => {
    confirm({
      title: '完全に削除してもよろしいですか？',
      description: 'この操作の後に在庫を復元することはできません。',
      cancellationText: 'キャンセル',
    })
      .then(async () => {
        await deleteDoc(`accounts/${account.id}/stocks/${data.id}`)
        setOpen(false)
        onDelete()
      })
      .catch(() => {})
  }

  const handleToggleArchive = async () => {
    const deleted = !data.deleted
    await updateDoc(`accounts/${account.id}/stocks/${data.id}`, { deleted })
    setOpen(false)
    if (deleted) {
      onArchive()
    } else {
      onRecover()
    }
  }

  const handleDetect = async code => {
    setValue('code', code)
    if (!getValues('name') && !loading) {
      setLoading(true)
      await client
        .post('/api/check', { code })
        .then(res => res.data)
        .then(data => {
          Object.entries(data).forEach(([key, value]) => setValue(key, value))
        })
      setLoading(false)
    }
  }

  const handleCheckCode = () => {
    if (getValues('name')) return

    const code = getValues('code')
    if (/^4[59]([0-9]{11}|[0-9]{6})$/.test(code)) {
      client
        .post('/api/check', { code })
        .then(res => res.data)
        .then(data => {
          Object.entries(data).forEach(([key, value]) => setValue(key, value))
        })
    }
  }

  const handleChangeImage = url => {
    setValue('image', url)
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} TransitionProps={{ onExited: onClose }}>
      <DialogTitle>在庫登録</DialogTitle>
      <DialogContent
        sx={{
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ImageUploader
          path={`accounts/${account.id}/stocks/${data.id || id}`}
          onChange={handleChangeImage}
          value={image}
          error={errors.image}
        />
        {admin && (
          <Typography sx={{ mb: 2 }} variant="body2">
            アカウント：
            <Link to={`/accounts/${account.id}`}>
              {account.name} ( {account.id} )
            </Link>
          </Typography>
        )}
        <Controller
          name="code"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="JANコード"
              autoComplete="off"
              sx={{ mb: 2 }}
              onKeyDown={e => {
                // Enter で検索
                if (e.keyCode === 13) {
                  handleCheckCode()
                }
              }}
              InputProps={{
                onBlur: handleCheckCode,
                endAdornment: isMobile && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setReaderOpen(true)}
                      onMouseDown={event => event.preventDefault()}
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Controller
          name="name"
          defaultValue=""
          rules={{ required: true, validate: validateEmptyString }}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="商品名"
              autoComplete="off"
              sx={{ mb: 2 }}
              disabled={loading}
              InputProps={{
                endAdornment: loading && (
                  <InputAdornment style={{ marginRight: 12 }}>
                    <CircularProgress size={24} />
                  </InputAdornment>
                ),
              }}
              error={Boolean(errors.name)}
              helperText={Boolean(errors.name) && '必須項目です'}
            />
          )}
        />
        <Controller
          name="category"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="カテゴリー"
              sx={{ mb: 2 }}
              select
              SelectProps={{ native: true }}
            >
              {categories.map(x => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="standard"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="規格"
              placeholder="○個入り・○枚入り・○○味"
              autoComplete="off"
              sx={{ mb: 2 }}
            />
          )}
        />
        <Controller
          name="price"
          defaultValue=""
          control={control}
          rules={{ required: true, validate: validateNaturalNumber }}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              label="単価"
              autoComplete="off"
              sx={{ mb: 2 }}
              error={Boolean(errors.price)}
              helperText={Boolean(errors.price) && '入力された値が不正です'}
            />
          )}
        />
        <Controller
          name="amount"
          defaultValue=""
          control={control}
          rules={{ required: true, validate: validateNaturalNumber }}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              label="数量"
              autoComplete="off"
              sx={{ mb: 2 }}
              error={Boolean(errors.amount)}
              helperText={Boolean(errors.amount) && '入力された値が不正です'}
            />
          )}
        />
        <Controller
          name="purchase"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField {...field} label="購入先" autoComplete="off" sx={{ mb: 2 }} />
          )}
        />
        {account.role !== Role.LOCATION_MEMBER.value && (
          <Controller
            name="locationId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="拠点"
                sx={{ mb: 2 }}
                select
                SelectProps={{ native: true }}
              >
                {activeLocations
                  .sort((a, b) => {
                    a.category = a.category || ''
                    b.category = b.category || ''
                    return a.category === b.category
                      ? a.name > b.name
                        ? 1
                        : -1
                      : a.category && b.category
                      ? a.category > b.category
                        ? 1
                        : -1
                      : b.category
                      ? 1
                      : -1
                  })
                  .map(x => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </TextField>
            )}
          />
        )}
        <Controller
          name="storage"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField {...field} label="保管場所" autoComplete="off" sx={{ mb: 2 }} />
          )}
        />
        <Controller
          name="expire"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              InputLabelProps={{ shrink: true }}
              type="date"
              label="消費期限"
              autoComplete="off"
              sx={{ mb: 2 }}
              error={Boolean(errors.expire)}
              helperText={Boolean(errors.expire) && '正しい日付で入力してください'}
            />
          )}
        />
        <Controller
          name="memo"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField {...field} label="備考欄" autoComplete="off" multiline sx={{ mb: 2 }} />
          )}
        />
      </DialogContent>
      <DialogActions>
        {data.id ? (
          data.deleted ? (
            <>
              <Button color="primary" onClick={handleToggleArchive}>
                復元する
              </Button>
              <Button color="secondary" onClick={handleDelete}>
                完全に削除
              </Button>
            </>
          ) : (
            <Button color="secondary" onClick={handleToggleArchive}>
              削除
            </Button>
          )
        ) : (
          <FormControlLabel
            sx={{
              pl: 2,
              '& .MuiFormControlLabel-label': { fontSize: 14 },
            }}
            control={
              <Checkbox
                checked={isContinuous}
                onChange={() => setIsContinuous(v => !v)}
                color="primary"
              />
            }
            label="続けて登録する"
          />
        )}
        <div style={{ flex: 1 }} />
        <Button color="primary" onClick={() => setOpen(false)} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {data.id ? '更新する' : '登録する'}
        </Button>
      </DialogActions>
      <JanReader open={readerOpen} onClose={() => setReaderOpen(false)} onDetect={handleDetect} />
    </Dialog>
  )
}
